import { Inject, Injectable } from '@angular/core';
import {
  AuthHeaders,
  BaseApiService,
  CoreInjectorTokens,
  Environment,
  PageableListV2Dto,
  ResponseDto,
} from '@fleet-beat/core';
import { catchError, map, Observable } from 'rxjs';
import { GetManyNotificationsDto, NotificationDto } from '../../models';
import { HttpErrorResponse } from '@angular/common/http';

interface MarkNotificationsAsReadDto {
  ids: string[];
}

interface ReadAllNotificationsDto {
  companyIds?: string[];
}

@Injectable({
  providedIn: 'root',
})
export class NotificationsApiService {
  private readonly baseUrl: string = `${this.environment.apiEndpoint}/notifications`;

  constructor(
    @Inject(CoreInjectorTokens.Environment) private environment: Environment,
    private baseApiService: BaseApiService,
  ) {}

  getMany(params: GetManyNotificationsDto, auth: AuthHeaders): Observable<PageableListV2Dto<NotificationDto>> {
    return this.baseApiService.get(this.baseUrl, { auth, params }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<NotificationDto[]>) => this.baseApiService.mapResponseV2(response)),
      map((response: ResponseDto<NotificationDto[]>) => {
        return {
          data: response.data,
          ...response.meta,
        };
      }),
    );
  }

  markAsRead(data: MarkNotificationsAsReadDto, auth: AuthHeaders): Observable<NotificationDto[]> {
    return this.baseApiService.patch(`${this.baseUrl}/read`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map((response: ResponseDto<NotificationDto[]>) => this.baseApiService.mapResponse(response)),
    );
  }

  readAll(data: ReadAllNotificationsDto, auth: AuthHeaders): Observable<void> {
    return this.baseApiService.patch(`${this.baseUrl}/read-all`, data, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map(() => void 0),
    );
  }

  deleteAll(auth: AuthHeaders): Observable<void> {
    return this.baseApiService.delete(`${this.baseUrl}/all`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map(() => void 0),
    );
  }

  deleteById(id: string, auth: AuthHeaders): Observable<void> {
    return this.baseApiService.delete(`${this.baseUrl}/${id}`, { auth }).pipe(
      catchError((error: HttpErrorResponse) => this.baseApiService.catchApiError(error)),
      map(() => void 0),
    );
  }
}
